// src/pages/ServiceDetail/components/StatusTab.tsx

import React from 'react';
import { AnyService } from '../../../types/service.type';

interface StatusTabProps {
  service: AnyService;
}

const StatusTab: React.FC<StatusTabProps> = ({ service }) => {
  const healthMetrics = [
    {
      label: 'CPU Usage',
      value: '45%',
      status: 'healthy',
      details: 'Average over last 5 minutes',
    },
    {
      label: 'Memory Usage',
      value: '62%',
      status: 'warning',
      details: 'Consider upgrading if sustained',
    },
    {
      label: 'Disk Usage',
      value: '38%',
      status: 'healthy',
      details: '3.8 GB of 10 GB used',
    },
    {
      label: 'Response Time',
      value: '245ms',
      status: 'healthy',
      details: 'Average response time',
    },
  ];

  const environmentDetails = [
    { label: 'Instance Type', value: service.instanceType },
    { label: 'Region', value: service.region },
    { label: 'Environment', value: service.environment },
    { label: 'Platform', value: service.type === 'wordpress' ? 'WordPress' : 'Custom' },
  ];

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'healthy':
        return 'bg-green-100 text-green-700 border-green-200';
      case 'warning':
        return 'bg-yellow-100 text-yellow-700 border-yellow-200';
      case 'error':
        return 'bg-red-100 text-red-700 border-red-200';
      default:
        return 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-700';
    }
  };

  return (
    <div className="space-y-6">
      {/* Health Metrics */}
      <div>
        <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-4">Health Metrics</h3>
        <div className="grid grid-cols-2 gap-4">
          {healthMetrics.map((metric, index) => (
            <div
              key={index}
              className={`border rounded-lg p-4 ${getStatusColor(metric.status)}`}
            >
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium">{metric.label}</span>
                <span className="text-lg font-bold">{metric.value}</span>
              </div>
              <p className="text-xs opacity-80">{metric.details}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Environment Details */}
      <div>
        <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-4">
          Environment Details
        </h3>
        <div className="bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
          <table className="w-full">
            <tbody className="divide-y divide-gray-200 dark:divide-gray-600">
              {environmentDetails.map((detail, index) => (
                <tr key={index}>
                  <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400 font-medium w-1/3">
                    {detail.label}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900 dark:text-white capitalize">
                    {detail.value}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Capacity Information */}
      <div>
        <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-4">
          Capacity Information
        </h3>
        <div className="grid grid-cols-3 gap-4">
          <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 text-center">
            <p className="text-2xl font-bold text-gray-900 dark:text-white">1</p>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">Running Instances</p>
          </div>
          <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 text-center">
            <p className="text-2xl font-bold text-gray-900 dark:text-white">1</p>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">Desired Instances</p>
          </div>
          <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 text-center">
            <p className="text-2xl font-bold text-gray-900 dark:text-white">3</p>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">Max Instances</p>
          </div>
        </div>
      </div>

      {/* Recent Activity */}
      <div>
        <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-4">Recent Activity</h3>
        <div className="space-y-3">
          {[
            {
              time: '2 hours ago',
              action: 'Service health check passed',
              status: 'success',
            },
            {
              time: '5 hours ago',
              action: 'Memory usage exceeded 80%',
              status: 'warning',
            },
            {
              time: '1 day ago',
              action: 'SSL certificate renewed',
              status: 'success',
            },
          ].map((activity, index) => (
            <div
              key={index}
              className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-700"
            >
              <span
                className={`w-2 h-2 rounded-full flex-shrink-0 ${
                  activity.status === 'success' ? 'bg-green-500' : 'bg-yellow-500'
                }`}
              ></span>
              <div className="flex-1">
                <p className="text-sm text-gray-900 dark:text-white">{activity.action}</p>
                <p className="text-xs text-gray-500 mt-0.5">{activity.time}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default StatusTab;